<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-chart-line</v-icon>
                Daily Attendance Summary Report
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-form @submit.prevent="fetchSummary">
                  <v-row class="py-3 px-3" align="end" dense>

                    <v-col cols="12" md="3">
                      <v-date-input
                          input-format="dd/mm/yyyy"
                          v-model="filters.from_date"
                          label="From Date"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          hide-details="auto"
                          clearable
                          :max="filters.to_date"
                      />
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-date-input
                          input-format="dd/mm/yyyy"
                          v-model="filters.to_date"
                          label="To Date"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          hide-details="auto"
                          clearable
                          :min="filters.from_date"
                      />
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-autocomplete
                          v-model="filters.employee_id"
                          :items="employees"
                          :item-title="item => `${item.name}-(${item.code})` ?? 'N/A'"
                          item-value="id"
                          label="Employee (Optional)"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          :loading="employeesLoading"
                          clearable
                          hide-details="auto"
                          placeholder="Search employee"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>

                    <v-col cols="12" md="3" class="d-flex align-end">
                      <v-btn
                          type="submit"
                          color="primary"
                          variant="flat"
                          :loading="loadingSummary"
                          prepend-icon="mdi-magnify"
                          density="compact"
                          height="40"
                      >
                        Search
                      </v-btn>
                    </v-col>

                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <v-col cols="12">
          <v-expansion-panels v-model="reportPanel" class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-title
                  color="blue-grey-lighten-4"
                  class="panel-header"
                  expand-icon="mdi-chevron-down"
              >
                <v-icon class="me-2">mdi-table</v-icon>
                Summary Results
              </v-expansion-panel-title>

              <v-expansion-panel-text>

                <v-row class="py-2">

                  <v-col md="4" cols="12">
                    <v-text-field
                        v-model="search"
                        append-inner-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                        density="compact"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        class="pl-2"
                        clearable
                    />
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                          v-if="summaryData.length > 0"
                          variant="outlined"
                          density="compact"
                          size="x-small"
                          divided
                      >

                        <v-btn
                            prepend-icon="mdi mdi-download"
                            variant="flat"
                            color="success"
                            @click="exportToExcel"
                            :disabled="!summaryData.length"
                        >
                          EXCEL
                        </v-btn>

                        <v-btn
                            prepend-icon="mdi mdi-download"
                            variant="flat"
                            color="red"
                            @click="exportToPDF"
                            :disabled="!summaryData.length"
                        >
                          PDF
                        </v-btn>

                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12"></v-col>

                </v-row>

                <v-alert
                    v-if="!loadingSummary && filteredSummaryData.length === 0"
                    type="info"
                    variant="tonal"
                >
                  No summary data found for the selected criteria.
                </v-alert>

                <v-progress-linear
                    v-if="loadingSummary"
                    indeterminate
                    color="primary"
                    class="mb-4"
                />

                <div v-if="filteredSummaryData.length">

                  <v-table density="comfortable" class="table-bordered">

                    <thead>
                    <tr>
                      <th># SL</th>
                      <th>Date</th>
                      <th style="text-align: center;">Total Empl.</th>
                      <th style="text-align: center;">Present</th>
                      <th style="text-align: center;">Absent</th>
                      <th style="text-align: center;">Late</th>
                      <th style="text-align: center;">Early</th>
                      <th style="text-align: center;">Late & Early</th>
                      <th style="text-align: center;">On Leave</th>
                      <th style="text-align: center;">Weekend / Holiday</th>
                      <th style="text-align: center;">Total OT (hrs)</th>
                    </tr>
                    </thead>

                    <tbody>
                    <tr
                        v-for="(row, index) in filteredSummaryData"
                        :key="row.date"
                    >
                      <td>{{ index + 1 }}</td>

                      <td>
                        {{ formatDateView(row.date) }}
                      </td>

                      <td style="text-align: center;">
                        {{ formatNumber(row.total_employees) }}
                      </td>

                      <td style="text-align: center;">
                        {{ formatNumber(row.present) }}
                      </td>

                      <td style="text-align: center;">
                        {{ formatNumber(row.absent) }}
                      </td>

                      <td style="text-align: center;">
                        {{ formatNumber(row.late) }}
                      </td>

                      <td style="text-align: center;">
                        {{ formatNumber(row.early) }}
                      </td>

                      <td style="text-align: center;">
                        {{ formatNumber(row.late_and_early) }}
                      </td>

                      <td style="text-align: center;">
                        {{ formatNumber(row.on_leave) }}
                      </td>

                      <td style="text-align: center;">
                        {{ formatNumber(row.weekend_holiday) }}
                      </td>

                      <td style="text-align: center;">
                        {{ formatNumber(row.total_ot_hours, 2) }}
                      </td>
                    </tr>
                    </tbody>

                    <tfoot v-if="filteredSummaryData.length > 0">
                    <tr style="font-weight: bold; color: #000000;">

                      <td
                          colspan="2"
                          style="text-align: right; padding: 12px;"
                      >
                        Total:
                      </td>

                      <td style="text-align: center; padding: 12px;">
                        {{ formatNumber(filteredTotals.total_employees) }}
                      </td>

                      <td style="text-align: center; padding: 12px;">
                        {{ formatNumber(filteredTotals.present) }}
                      </td>

                      <td style="text-align: center; padding: 12px;">
                        {{ formatNumber(filteredTotals.absent) }}
                      </td>

                      <td style="text-align: center; padding: 12px;">
                        {{ formatNumber(filteredTotals.late) }}
                      </td>

                      <td style="text-align: center; padding: 12px;">
                        {{ formatNumber(filteredTotals.early) }}
                      </td>

                      <td style="text-align: center; padding: 12px;">
                        {{ formatNumber(filteredTotals.late_and_early) }}
                      </td>

                      <td style="text-align: center; padding: 12px;">
                        {{ formatNumber(filteredTotals.on_leave) }}
                      </td>

                      <td style="text-align: center; padding: 12px;">
                        {{ formatNumber(filteredTotals.weekend_holiday) }}
                      </td>

                      <td style="text-align: center; padding: 12px;">
                        {{ formatNumber(filteredTotals.total_ot_hours, 2) }}
                      </td>

                    </tr>
                    </tfoot>

                  </v-table>

                </div>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

      </v-row>
    </v-container>
  </v-app>
</template>


<script setup>
import {
  ref,
  reactive,
  computed,
  onMounted,
  getCurrentInstance
} from 'vue'

import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import {
  formatDateView,
  formatDateDash
} from "@/plugins/helpers"


const store = useStore()

const instance = getCurrentInstance()
const appContext = instance?.appContext

const employees = ref([])
const employeesLoading = ref(false)
const loadingSummary = ref(false)
const summaryData = ref([])
const panel = ref([0])
const reportPanel = ref([0])
const search = ref('')

const filters = reactive({
  from_date: '',
  to_date: '',
  employee_id: null
})

const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})
const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})

const formatEmployeeLabel = item => {
  if (!item) return ''

  return `${item.name || ''} ${
      item.employee_code
          ? `- ${item.employee_code}`
          : ''
  }`.trim()
}

const formatNumber = (value, fractionDigits = 0) => {
  if (
      value === undefined ||
      value === null ||
      isNaN(value)
  ) {
    return '-'
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })
}

const filteredSummaryData = computed(() => {

  if (!search.value) {
    return summaryData.value
  }

  const searchLower = search.value.toLowerCase()
  return summaryData.value.filter(row => {
    const date = formatDateView(row.date).toLowerCase()
    return date.includes(searchLower)
  })
})

const filteredTotals = computed(() => {

  return filteredSummaryData.value.reduce(
      (acc, row) => {

        acc.total_employees +=
            Number(row.total_employees) || 0

        acc.present +=
            Number(row.present) || 0

        acc.absent +=
            Number(row.absent) || 0

        acc.late +=
            Number(row.late) || 0

        acc.early +=
            Number(row.early) || 0

        acc.late_and_early +=
            Number(row.late_and_early) || 0

        acc.on_leave +=
            Number(row.on_leave) || 0

        acc.weekend_holiday +=
            Number(row.weekend_holiday) || 0

        acc.total_ot_hours +=
            Number(row.total_ot_hours) || 0

        return acc
      },
      {
        total_employees: 0,
        present: 0,
        absent: 0,
        late: 0,
        early: 0,
        late_and_early: 0,
        on_leave: 0,
        weekend_holiday: 0,
        total_ot_hours: 0
      }
  )
})

const setDefaultDates = () => {

  const today = new Date()

  const startOfMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
  )

  filters.from_date = startOfMonth
  filters.to_date = today
}

const loadEmployees = async () => {

  employeesLoading.value = true

  try {

    const response = await store.dispatch('getEmployees')
    employees.value = response.data.data || []
  } catch (error) {
    toast.error( error.message || 'Failed to load employees' )
    employees.value = []
  } finally {
    employeesLoading.value = false
  }
}

const fetchSummary = async () => {

  if ( !filters.from_date || !filters.to_date ) {
    toast.warning('Please select both From Date and To Date')
    return
  }

  loadingSummary.value = true

  try {
    const params = {
      from_date: formatDateDash(filters.from_date),
      to_date: formatDateDash(filters.to_date),
      employee_id: filters.employee_id || undefined
    }
    const response =await store.dispatch('getAttendanceSummary', params)

    if (response.success) {

      summaryData.value =
          response.data || []

    } else {

      summaryData.value = []

      toast.error(
          response.message ||
          'Failed to fetch summary report'
      )
    }

  } catch (error) {

    summaryData.value = []

    toast.error(
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch summary report'
    )

  } finally {

    loadingSummary.value = false

  }
}

const exportToExcel = () => {

  if (!filteredSummaryData.value.length) {

    toast.warning('No data to export')

    return
  }


  const data = [

    [
      'Daily Attendance Summary Report'
    ],

    [
      `From Date: ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}`
    ]

  ]


  if (filters.employee_id) {

    const selectedEmployee =
        employees.value.find(
            e => e.id === filters.employee_id
        )

    if (selectedEmployee) {

      data.push([
        `Employee: ${
            selectedEmployee.name || ''
        }${
            selectedEmployee.employee_code
                ? ` (${selectedEmployee.employee_code})`
                : ''
        }`
      ])

    }
  }

  data.push(
      [],
      [
        '# SL',
        'Date',
        'Total Empl.',
        'Present',
        'Absent',
        'Late',
        'Early',
        'Late & Early',
        'On Leave',
        'Weekend / Holiday',
        'Total OT (hrs)'
      ],

      []
  )

  filteredSummaryData.value.forEach(
      (row, index) => {

        data.push([
          index + 1,
          formatDateView(row.date),
          formatNumber(row.total_employees),
          formatNumber(row.present),
          formatNumber(row.absent),
          formatNumber(row.late),
          formatNumber(row.early),
          formatNumber(row.late_and_early),
          formatNumber(row.on_leave),
          formatNumber(row.weekend_holiday),
          formatNumber(row.total_ot_hours,2)
        ])

      }
  )

  if (filteredSummaryData.value.length > 0) {

    data.push([])

    data.push([

      '',

      'Total:',

      formatNumber(
          filteredTotals.value.total_employees
      ),

      formatNumber(
          filteredTotals.value.present
      ),

      formatNumber(
          filteredTotals.value.absent
      ),

      formatNumber(
          filteredTotals.value.late
      ),

      formatNumber(
          filteredTotals.value.early
      ),

      formatNumber(
          filteredTotals.value.late_and_early
      ),

      formatNumber(
          filteredTotals.value.on_leave
      ),

      formatNumber(
          filteredTotals.value.weekend_holiday
      ),

      formatNumber(
          filteredTotals.value.total_ot_hours,
          2
      )

    ])
  }


  const proxy =
      appContext?.config?.globalProperties


  if (
      proxy?.$exportTableToExcel &&
      proxy?.$fileDate
  ) {

    proxy.$exportTableToExcel(
        data,
        'attendance_summary_report_' +
        proxy.$fileDate()
    )
  }
}

const exportToPDF = async () => {

  if (!filteredSummaryData.value.length) {

    toast.warning(
        'No data to export'
    )

    return
  }


  const company =
      getCurrentCompany.value
console.log(company)
  const fileServerUrl =
      getFileServerUrl.value


  const selectedEmployee =
      filters.employee_id
          ? employees.value.find(
              e => e.id === filters.employee_id
          )
          : null


  const printWindow = window.open(
      '',
      '_blank',
      `width=${screen.width}, height=${screen.height}`
  )


  const printContent = `

    <!DOCTYPE html>

    <html>

      <head>

        <meta charset="UTF-8">

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        >

        <meta
          http-equiv="X-UA-Compatible"
          content="ie=edge"
        >

        <title>
          Daily Attendance Summary Report
        </title>


        <link
          href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css"
          rel="stylesheet"
        >

        <style>

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          @page {
            size: A4;
            margin: 0;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: auto;
          }

          body {
            font-family: Arial, Helvetica, sans-serif;
            background: #fff;
            color: #000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .report-container {
            width: 100%;
          }

          table.master-table {
            width: 100%;
            border-collapse: collapse;
          }

          .company-info {
            flex: 1;
            min-width: 0;
          }

          .company-name {
            font-size: 13px;
            font-weight: bold;
            text-transform: uppercase;
            margin-bottom: 4px;
          }

          .company-text {
            font-size: 11px;
            margin: 2px 0;
            line-height: 1.35;
          }

          .logo-container {
            width: 160px;
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
          }

          .logo-container img {
            max-height: 75px;
            max-width: 150px;
            object-fit: contain;
          }

          .header-row td {
            padding: 15px 32px 10px 32px;
            border-bottom: 2px solid #eee;
          }

          .footer-row td {
            padding: 8px 32px 10px 32px;
            border-top: 1px solid #eee;
          }

          .invoice-footer {
            margin-top: auto;
            position: fixed;
            left: 0;
            bottom: 0px;
            width: 100%;
          }

          .invoice-footer p {
            font-size: 11px;
            font-weight: 500;
            margin: 3px 0;
            display: flex;
            align-items: center;
            gap: 4px;
            line-height: 1.25;
          }

          .report-title {
            font-size: 16px;
            text-align: center;
            text-decoration: underline;
            font-weight: bold;
            margin: 10px 0 15px 0;
          }

          .report-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 12px;
            font-size: 12px;
          }

          .report-info-left {
            text-align: left;
          }

          .report-info-right {
            text-align: right;
          }

          .report-info p {
            margin: 3px 0;
          }

          table.attendance-table {
            width: 100%;
            border-collapse: collapse;
            border-spacing: 0;
            font-size: 10px;
            background: #fff;
          }

          .attendance-table thead {
            display: table-header-group;
          }


          .attendance-table tfoot {
            display: table-footer-group;
          }

          .attendance-table th,
          .attendance-table td {
            border: 1px solid #000;
            padding: 5px 4px;
            vertical-align: middle;
          }

          .attendance-table th {
            background: #e0e0e0;
            text-align: center;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .attendance-table td {
            text-align: left;
          }


          .text-center {
            text-align: center;
          }

          .text-right {
            text-align: right;
          }

          .total-row {
            font-weight: bold;
            background: #f5f5f5;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .total-row td {
            font-weight: bold;
          }

          .attendance-table tr {
            page-break-inside: avoid;
          }

          .attendance-table {
            page-break-inside: auto;
          }


          .attendance-table tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          @media print {
            body {
              margin: 0;
              padding: 0;
            }

            .header-row {
              display: table-header-group;
            }

            .footer-row {
              display: table-footer-group;
            }

            .attendance-table thead {
              display: table-header-group;
            }

            .attendance-table tr {
              page-break-inside: avoid;
            }

          }

        </style>

      </head>


      <body>

        <div class="report-container">
          <table class="master-table">
            <thead>
              <tr>
                <td colspan="100%">
                  <div>
                    <div style="display: flex; justify-content: flex-end; align-items: flex-start; padding-bottom: 10px; margin-bottom: 16px;">
                      <img
                        src="${fileServerUrl}${company.logo}"
                        class="logo-img"
                        alt="Logo"
                        style="max-height: 80px;"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </thead>

            <tbody>
              <tr class="body-row">
                <td>

                  <div class="report-title">
                    DAILY ATTENDANCE SUMMARY REPORT
                  </div>


                  <div class="report-info">
                    <div class="report-info-left">
                      <p>
                        <strong>Period:</strong> ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)} </p>

                      ${selectedEmployee
      ? `
                            <p>
                              <strong>Employee:</strong>
                              ${selectedEmployee.name || ''}
                              ${ selectedEmployee.employee_code
          ? ` (${selectedEmployee.employee_code})`
          : ''
      }
                            </p>
                          `
      : ''
  }

                    </div>

                    <div class="report-info-right">
                      <p><strong>Total Records:</strong>${filteredSummaryData.value.length}</p>
                    </div>

                  </div>

                  <table class="attendance-table">
                    <thead>
                      <tr>
                        <th># SL</th>
                        <th>Date</th>
                        <th>Total Empl.</th>
                        <th>Present</th>
                        <th>Absent</th>
                        <th>Late</th>
                        <th>Early</th>
                        <th>Late &amp; Early</th>
                        <th>On Leave</th>
                        <th>Weekend / Holiday</th>
                        <th>Total OT (hrs)</th>
                      </tr>
                    </thead>

                    <tbody>
                      ${filteredSummaryData.value.map((row, index) => `
                        <tr>
                          <td class="text-center">${index + 1}</td>
                          <td>${formatDateView(row.date)}</td>
                          <td class="text-center">${formatNumber(row.total_employees)}</td>
                          <td class="text-center">${formatNumber(row.present)}</td>
                          <td class="text-center">${formatNumber(row.absent)}</td>
                          <td class="text-center">${formatNumber(row.late)}</td>
                          <td class="text-center">${formatNumber(row.early)}</td>
                          <td class="text-center">${formatNumber(row.late_and_early)}</td>
                          <td class="text-center">${formatNumber(row.on_leave)}</td>
                          <td class="text-center">${formatNumber(row.weekend_holiday)}</td>
                          <td class="text-center">${formatNumber(row.total_ot_hours, 2)}</td>
                        </tr>
                      `).join('')}
                    </tbody>

                    <tfoot>
                      <tr class="total-row">
                        <td colspan="2" class="text-right">Total:</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.total_employees)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.present)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.absent)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.late)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.early)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.late_and_early)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.on_leave)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.weekend_holiday)}</td>
                        <td class="text-center">${formatNumber(filteredTotals.value.total_ot_hours, 2)}</td>
                      </tr>
                    </tfoot>
                  </table>

                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td colspan="100%" style="border:none">
                  <div style="width:100%;height: 100px;">&nbsp;</div>
                </td>
              </tr>
              <tr class="footer-row">
                <td>

                  <div class="invoice-footer" v-if="invoiceHeader" style="margin-left:10px; margin-bottom:10px; color: #343591;">
                <p style="font-size: 13px; font-weight: 500; display: flex; align-items: center; gap:4px">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" d="M12 11.5A2.5 2.5 0 0 1 9.5 9A2.5 2.5 0 0 1 12 6.5A2.5 2.5 0 0 1 14.5 9a2.5 2.5 0 0 1-2.5 2.5M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7" />
                  </svg>

                  <span>${company?.address}</span>
                </p>

                <p style="font-size: 13px; font-weight: 500; display: flex; align-items: center; gap:4px">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24c1.12.37 2.33.57 3.57.57c.55 0 1 .45 1 1V20c0 .55-.45 1-1 1c-9.39 0-17-7.61-17-17c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1c0 1.25.2 2.45.57 3.57c.11.35.03.74-.25 1.02z" />
                  </svg>

                  <strong>Phone: </strong>  +880 9610-993393, +880 2 222288648, www.remexingredient.com | www.remexcorp.com
                </p>
              </div>
                </td>
              </tr>
            </tfoot>
          </table>

        </div>

      </body>

    </html>
  `


  printWindow.document.write(
      printContent
  )

  printWindow.document.close()

  printWindow.focus()

  await new Promise(resolve =>
      setTimeout(resolve, 700)
  )

  printWindow.print()
  printWindow.close()
}


onMounted(async () => {

  setDefaultDates()

  await loadEmployees()

})
</script>


<style scoped>

.gap-2 {
  gap: 8px;
}


.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}


.table-bordered :deep(table) {
  border-collapse: collapse;
}


.panel-header {
  font-weight: 600;
}

</style>